/**
 * useMetricFormatter — wraps `formatUnit` so consumers don't reach into
 * the store for a pure formatter call. `formatUnit` runs through
 * SI-decade ladders + a variant-aware `FormatPolicy` (DPI locks
 * throughput to Mbps).
 *
 * This composable adds three consumer-side ergonomics on top:
 *
 *   - `value == null` → returns `{ amount: "", unit: defaultUnit }` so
 *     placeholder pills render the unit label without an amount instead
 *     of an empty string for both fields.
 *   - the return shape is the projected `{ amount, unit }` pair the pill
 *     and stacked-rows consumers need; the `value` field stays inside
 *     `formatUnit` for hysteresis paths that already reach for it directly.
 *   - the variant policy is derived internally from `useVariantStore`,
 *     so consumers don't need to know about variant semantics.
 *
 * Retires `MetricPillCluster.formatPill` (the pre-T workaround for the
 * jitter bug) and the per-consumer `formattedFor`/`unitFor` helpers.
 */

import {
    METRIC_DEFINITIONS,
    type MetricId,
} from "@src/types/speedtest.ts";
import { formatUnit } from "@features/speedtest/state/metric-format.ts";
import { policyForVariant } from "@features/speedtest/state/result-bridge.ts";
import { useVariantStore } from "@src/stores/useVariantStore.ts";

export interface FormattedMetric {
    amount: string;
    unit: string;
}

export interface UseMetricFormatter {
    /** Format a numeric value for display. Null collapses to an empty
     *  amount + the metric's default unit. */
    format: (id: MetricId, value: number | null | undefined) => FormattedMetric;
}

export function useMetricFormatter(): UseMetricFormatter {
    const variantStore = useVariantStore();

    function format(id: MetricId, value: number | null | undefined): FormattedMetric {
        if (value == null) {
            return { amount: "", unit: METRIC_DEFINITIONS[id].defaultUnit };
        }
        const info = formatUnit(id, value, policyForVariant(variantStore.variant));
        return {
            amount: info.amount ?? "",
            unit: info.unit ?? METRIC_DEFINITIONS[id].defaultUnit,
        };
    }

    return { format };
}
